import { WebsocketEventBus } from './websocket_bus';

export enum CommandType {
    Subscribe = "subscribe",
    Unsubscribe = "unsubscribe",
    Message = "message",
    Activity= "activity",
    Authenticate = "authenticate",
    Ping = "ping",
}

export enum MessageType {
    Welcome = "welcome",
    Ping = "ping",
    ConfirmSubscription = "confirm_subscription",
    ConfirmUnsubscription = "confirm_unsubscription",
    RejectSubscription = "reject_subscription",
    RejectUnsubscription = "reject_unsubscription",
    Broadcast = "broadcast",
    Error = "error",
    Notice = "notice",
    AuthenticateChallenge = "authenticate_challenge",
    Authenticated = "authenticated",
}

export interface ChannelIdentity {
    ID: string | null;
    Type: string;
}

export interface Message {
    ID: string;
    Type: MessageType | string;
    Channel?: ChannelIdentity;
    Data?: any;
    ErrorMessage?: string;
}

export interface Command {
    Channel?: ChannelIdentity;
    Command: string | CommandType;
    Data?: any;
}

export interface Options {
    Url: string;
    DisablePing?: boolean;
    Bus?: WebsocketEventBus;
    OnOpen?: () => void;
    OnClose?: () => void;
    OnError?: () => void;
}

function noop() {}

const PingInterval = 20 * 1000;
const RetryInterval = 5 * 1000;

export class WebsocketClient {
    private socket: WebSocket | null;
    private retryHandle: any;
    private pinger: any;
    private keepAlive: boolean;
    private url: string;
    private creds: any;
    private bus: WebsocketEventBus;
    private isOpen: boolean = false;
    private onOpen: () => void | null;
    private onClose: () => void | null;
    private onError: (err: any) => void | null;
    private allowRetry: boolean = false;

    constructor(options: Options) {
        this.url = options.Url;
        this.keepAlive = !options.DisablePing;
        this.socket = null;
        this.bus = options.Bus || new WebsocketEventBus();
        this.onOpen = options.OnOpen || noop;
        this.onClose = options.OnClose || noop;
        this.onError = options.OnError || noop;
    }

    public open(creds: any) {
        this.creds = creds;
        this.initSocket();
        this.retryConnect()
        this.setupPoller();
    }

    public close() {
        if (this.socket) {
            this.socket.onclose = () => {};
            this.socket.close();
            this.socket = null;
            this.cancelRetry();
            clearInterval(this.pinger);
        }
    }

    public updateCredentials(creds: any) {
        this.creds = creds;
        this.allowRetry = true;
        this.sendCommand({
            Command: CommandType.Authenticate,
            Data: this.creds,
        });
    }

    public initSocket(): boolean {
        if ((window as any).WebSocket) {
            if (!this.socket || (this.socket && this.allowRetry && this.socket.readyState === WebSocket.CLOSED)) {
                try {
                    this.socket = null;

                    // Connect to server
                    const socket = new WebSocket(this.url);

                    // Handle close
                    socket.onclose = () => {
                        this.socketClose()
                    };

                    // Handle message
                    socket.onmessage = (evt) => {
                        if (evt.data) {
                            const signal: any = JSON.parse(evt.data);
                            this.handleMessage(signal);
                        }
                    };

                    socket.onerror = (evt) => {
                        this.socketError(evt);
                    };

                    socket.onopen = () => { 
                        this.isOpen = true;
                        this.onOpen();
                    };

                    this.socket = socket;
                    return true;
                } catch (err) {
                    this.socketError(err);
                    return false;
                }
            }
        }
        return false;
    }

    public sendCommand(command: Command): boolean {
        if (this.socket && this.socket.readyState !== WebSocket.OPEN) {
            this.initSocket();
        }
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            this.socket.send(JSON.stringify(command));
            return true
        }
        return false;
    }

    public eventBus(): WebsocketEventBus {
        return this.bus;
    }

    private socketClose() {
        if (this.isOpen) {
            this.isOpen = false;
            this.onClose();
            this.retryConnect();
        }
    }

    private socketError(error: any) {
        this.onError(error);
        return;
    }

    private handleMessage(message: Message) {
        if (message.Type === MessageType.Ping) {
        } else if (message.Type === MessageType.Error) {
            console.error(message.ErrorMessage);
            return;
        } else if (message.Type === MessageType.Welcome) {
            this.allowRetry = true;
        } else if (message.Type === MessageType.AuthenticateChallenge) {
            this.allowRetry = false;
            this.sendCommand({
                Command: CommandType.Authenticate,
                Data: this.creds,
            });
            return;
        }
        this.bus.send({
            type: message.Type,
            channel: message.Channel,
        }, message);
    }

    public activity(channel: ChannelIdentity) {
        this.sendCommand({
            Command: CommandType.Message,
            Channel: channel,
        });
    }

    private retryConnect() {
        if (!this.retryHandle) {
            this.retryHandle = setInterval(() => {
                if (this.socket) {
                    this.initSocket()
                }
            }, RetryInterval);
        }
        this.initSocket();
    }

    private setupPoller() {
        if (this.pinger) { return }
        this.pinger = setInterval(() => {
            if (this.socket && this.socket.readyState === WebSocket.OPEN) {
                if (this.keepAlive) {
                    this.sendCommand({
                        Command: CommandType.Ping,
                    });
                }
            }           
        }, PingInterval);
    }

    private cancelRetry() {
        clearInterval(this.retryHandle);
        this.retryHandle = false;
    }

}
